// The review: what a newly landed source PROPOSES about the board.
//
// The whole point of this screen is that the matcher does not get to
// decide. So every row shows WHY it was paired (`via`), what would
// change if it is accepted, and lets the reviewer send it somewhere else
// or reject it outright. Same contract as the area-map migration screen,
// which these users have already learned.
import React, { useEffect, useState } from "react";
import { Table, Select, Tag, Button, Empty, Tooltip, Collapse, Checkbox, Spin, message } from "antd";
import { popupInStage } from "./stagePopup";
import {
  emptySelection, toggleCategory, toggleRow, categoryChecked, categoryPartial,
  selectionSummary, isEmpty as selectionEmpty,
} from "./reviewSelection";
import { systemLabel } from "./trackingTheme";
import { processingLabel, isProcessing, failureNote, canApplyGroup } from "./processingState";
import { getMatchRows, NO_CATEGORY } from "api/tracking/fieldTrackingApi";

const ACTION_LABEL = {
  link: "Link to this item",
  add: "Add as new",
  absent: "Not in this source",
  skip: "Ignore",
};

// How much of a guess this was. A reviewer skimming 200 rows needs to
// know where to spend their attention, and the score is the only honest
// signal of that.
function ViaTag({ via, score }) {
  if (!via) return <span style={{ color: "#AEAEAE", fontSize: 11 }}>—</span>;
  const strong = score >= 0.9;
  const weak = score < 0.5;
  return (
    <Tooltip getPopupContainer={popupInStage} title={`Confidence ${Math.round((score || 0) * 100)}%`}>
      <Tag color={strong ? "green" : weak ? "orange" : "blue"} style={{ margin: 0, fontSize: 11 }}>
        {via}
      </Tag>
    </Tooltip>
  );
}

// The rows of one proposal, a page at a time from the server.
//
// A proposal can be 35,000 rows. The counts in the header and the
// "not mentioned" sample come with the proposal; the rows the table
// shows are fetched as the reviewer pages, and never all at once.
function useMatchRows(projectId, match, pageSize, category) {
  const [state, setState] = useState({ rows: match.rows || [], total: match.pageTotal || (match.rows || []).length, page: 1, loading: false });
  const load = async (page) => {
    if (!projectId) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      // category === null means every row. '' is the rows that have none, and
      // it travels as a sentinel because the query-string helper drops empty
      // values - otherwise "show me the uncategorised ones" would silently
      // become "show me everything".
      const catParam = category === null || category === undefined
        ? {} : { category: category === "" ? NO_CATEGORY : category };
      const r = await getMatchRows(projectId, match._id, { page, limit: pageSize, action: "!absent", ...catParam });
      setState({ rows: r.rows || [], total: r.total || 0, page, loading: false });
    } catch (e) {
      message.error(e.message || "Could not load the rows");
      setState((s) => ({ ...s, loading: false }));
    }
  };
  useEffect(() => { load(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [match._id, category]);
  return { ...state, load };
}

function ReviewTable({ projectId, match, columns, pageSize = 10, category = null, onRowsLoaded, rowSelection }) {
  const { rows, total, page, loading, load } = useMatchRows(projectId, match, pageSize, category);
  useEffect(() => { if (onRowsLoaded) onRowsLoaded(rows); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [rows]);
  return (
    <Table getPopupContainer={popupInStage}
      size="small" rowKey="observationKey" dataSource={rows} columns={columns} loading={loading}
      rowSelection={rowSelection}
      pagination={{
        current: page, pageSize, total, size: "small", showSizeChanger: false,
        showTotal: (n) => `${n.toLocaleString()} rows`,
        onChange: (p) => load(p),
      }}
      scroll={{ x: 840 }}
    />
  );
}

// canApply (2026-10-14): applying a proposal is a Construction tracking Edit;
// without the key the review is read-only. Absent = as before.
export default function TrackingReview({ projectId, matches, onApply, onApplyGroup = null, canApply = true }) {
  const [decisions, setDecisions] = useState({});   // matchId -> { key -> {action, itemId} }
  const [busy, setBusy] = useState("");
  // matchId -> { categories:Set, rows:Set }. Held HERE rather than in the
  // table so a tick survives paging and category switching - a reviewer who
  // ticks a row, pages, and comes back must find it still ticked.
  const [selection, setSelection] = useState({});
  // matchId -> the category the table is filtered to. null = every row.
  const [filter, setFilter] = useState({});
  // matchId -> the observation keys currently loaded, so a category can show
  // as PARTIAL when some of its visible rows are ticked.
  const [loaded, setLoaded] = useState({});
  const selOf = (id) => selection[id] || emptySelection();

  if (!matches || !matches.length) {
    return <Empty description="Nothing waiting on a review" />;
  }

  const setDecision = (matchId, key, patch) => setDecisions((prev) => ({
    ...prev,
    [matchId]: { ...(prev[matchId] || {}), [key]: { ...((prev[matchId] || {})[key] || {}), ...patch } },
  }));

  return (
    <Collapse defaultActiveKey={[String(matches[0]._id)]} accordion>
      {matches.map((m) => {
        // Counted on the server over every row, not over the page.
        const counts = m.counts || {};
        const absentCount = counts.absent || 0;
        const absentSample = m.absentSample || [];
        const mine = decisions[m._id] || {};
        const columns = [
          {
            title: "This source says", key: "obs", width: 210,
            render: (_, r) => (
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: "#23343E" }}>{r.observationLabel || "—"}</div>
                {r.observation && (
                  <div style={{ fontSize: 10.5, color: "#8D8D8D" }}>
                    {[systemLabel(r.observation.system), r.observation.size, r.observation.grid, r.observation.level]
                      .filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            ),
          },
          { title: "Why", key: "via", width: 130, render: (_, r) => <ViaTag via={r.via} score={r.score} /> },
          {
            title: "Decision", key: "action", width: 300,
            render: (_, r) => {
              const chosen = mine[r.observationKey] || {};
              const action = chosen.action || r.action;
              const itemId = chosen.itemId || (r.itemId ? String(r.itemId) : undefined);
              const options = [
                ...(r.itemId ? [{ value: String(r.itemId), label: `${r.itemLabel} (proposed)` }] : []),
                // `via` and `score` are OPTIONAL on a row now - a match
                // document stopped storing them when empty, because on
                // BA03 they were a third of a document that had 6,327
                // bytes of headroom left. The three other reads of these
                // fields already defaulted; this one did not, and an
                // absent one would have rendered "undefined NaN%" in a
                // dropdown the reviewer picks from.
                ...(r.alternatives || []).map((a) => ({
                  value: String(a.itemId),
                  label: [a.itemLabel, a.via, a.score ? `${Math.round(a.score * 100)}%` : '']
                    .filter(Boolean).join(' — '),
                })),
              ];
              return (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Select getPopupContainer={popupInStage}
                    size="small" style={{ width: 130 }} value={action}
                    onChange={(v) => setDecision(m._id, r.observationKey, { action: v })}
                    options={["link", "add", "skip"].map((a) => ({ value: a, label: ACTION_LABEL[a] }))}
                  />
                  {action === "link" && (
                    <Select getPopupContainer={popupInStage}
                      size="small" style={{ width: 160 }} value={itemId}
                      placeholder="pick an item"
                      onChange={(v) => setDecision(m._id, r.observationKey, { itemId: v })}
                      options={options}
                      notFoundContent="No candidate"
                    />
                  )}
                </div>
              );
            },
          },
          {
            title: "Would change", key: "changes", width: 200,
            render: (_, r) => (!r.changes || !r.changes.length
              ? <span style={{ color: "#AEAEAE", fontSize: 11 }}>nothing</span>
              : (
                <div style={{ fontSize: 11 }}>
                  {r.changes.map((c) => (
                    <div key={c.field} style={{ color: "#B84B0A" }}>
                      {c.field}: <b>{c.from || "—"}</b> → <b>{c.to}</b>
                    </div>
                  ))}
                </div>
              )),
          },
        ];

        return (
          <Collapse.Panel
            key={String(m._id)}
            header={(
              <span>
                <b>{m.sourceLabel || m.sourceKind}</b>
                <span style={{ color: "#8D8D8D", fontSize: 12 }}>
                  {" "}· {(counts.link || 0).toLocaleString()} matched,
                  {" "}{(counts.add || 0).toLocaleString()} new
                  {absentCount ? `, ${absentCount.toLocaleString()} not mentioned` : ""}
                </span>
                {/* ON THE HEADER, NOT ONLY INSIDE THE ROW. The owner's
                    words were "show a ui, saying next to it its being
                    processed" - and the whole failure was him reading a
                    list of proposals that looked un-actioned. A label he
                    has to expand a panel to find would not have helped. */}
                {isProcessing(m) ? (
                  <span style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "#1677FF" }}>
                    <Spin size="small" />
                    {processingLabel(m)}
                  </span>
                ) : null}
                {!isProcessing(m) && failureNote(m) ? (
                  <span style={{ marginLeft: 8, fontSize: 11.5, color: "#A8071A" }}>
                    · apply failed
                  </span>
                ) : null}
              </span>
            )}
          >
            {/* BY CATEGORY. The counts come from the server over the WHOLE
            proposal, not from the ten rows on screen - on SpaceX a proposal
            is 35,503 rows and page one is not a sample of anything.
            A category tick means EVERY row in that category, loaded or not;
            row ticks refine within what is loaded. */}
        {(() => {
          const cats = (m.categoryCounts || []).slice().sort((a, b) => (b.n || 0) - (a.n || 0));
          if (!cats.length) return null;
          const sel = selOf(m._id);
          const active = filter[m._id] === undefined ? null : filter[m._id];
          const loadedKeys = loaded[m._id] || {};
          const sum = selectionSummary(sel, cats);
          return (
            <div style={{ marginBottom: 10, padding: "8px 10px", background: "#F7F8F9", borderRadius: 4 }}>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
                {cats.map((c) => {
                  const label = c.category || "No category";
                  const keys = loadedKeys[c.category || ""] || [];
                  return (
                    <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                      <Checkbox
                        checked={categoryChecked(sel, c.category || "")}
                        indeterminate={categoryPartial(sel, c.category || "", keys)}
                        onChange={() => setSelection((p) => ({ ...p, [m._id]: toggleCategory(selOf(m._id), c.category || "") }))}
                      />
                      <a
                        onClick={() => setFilter((p) => ({ ...p, [m._id]: active === (c.category || "") ? null : (c.category || "") }))}
                        style={{
                          fontSize: 12.5,
                          fontWeight: active === (c.category || "") ? 700 : 500,
                          color: active === (c.category || "") ? "#23343E" : "#4A6572",
                        }}
                      >
                        {label}
                      </a>
                      <span style={{ fontSize: 11, color: "#8D8D8D" }}>{(c.n || 0).toLocaleString()}</span>
                    </span>
                  );
                })}
                {active !== null && (
                  <a onClick={() => setFilter((p) => ({ ...p, [m._id]: null }))} style={{ fontSize: 11.5 }}>
                    show all
                  </a>
                )}
              </div>
              {!selectionEmpty(sel) && (
                <div style={{ marginTop: 6, fontSize: 11.5, color: "#4A6572" }}>
                  {/* Two parts rather than one total: a ticked row may sit
                      inside a ticked category, and a combined number would
                      look precise without being so. */}
                  {sum.exact
                    ? `${sum.total} row${sum.total === 1 ? "" : "s"} selected`
                    : `${sum.categories} categor${sum.categories === 1 ? "y" : "ies"} (${sum.rowsFromCategories.toLocaleString()} rows)`
                      + (sum.looseRows ? ` and ${sum.looseRows} row${sum.looseRows === 1 ? "" : "s"} picked individually` : "")}
                </div>
              )}
            </div>
          );
        })()}
        <ReviewTable
          projectId={projectId}
          match={m}
          columns={columns}
          category={filter[m._id] === undefined ? null : filter[m._id]}
          onRowsLoaded={(rows) => setLoaded((p) => {
            const byCat = {};
            for (const r of rows || []) {
              const k = String(r.category || "");
              (byCat[k] = byCat[k] || []).push(r.observationKey);
            }
            return { ...p, [m._id]: { ...(p[m._id] || {}), ...byCat } };
          })}
          rowSelection={{
            selectedRowKeys: [...selOf(m._id).rows],
            onSelect: (record) => setSelection((p) => ({ ...p, [m._id]: toggleRow(selOf(m._id), record.observationKey) })),
            getCheckboxProps: (record) => ({
              // A row inside a ticked category is already covered; showing it
              // unticked would invite a reviewer to tick it again for nothing.
              disabled: categoryChecked(selOf(m._id), String(record.category || "")),
            }),
          }}
        />
            {!!absentCount && (
              <div style={{ marginTop: 8, fontSize: 11.5, color: "#8D8D8D" }}>
                {/* Said plainly, because "absent" reads like a deletion
                    and is not one. */}
                {/* THE COUNT AND HOW MANY ARE NAMED, both said. The proposal
                    keeps only a sample of these rows now (one per board
                    item was 300,000 rows per import part on BA03), so the
                    names below are never the whole list and must not read
                    as if they were. absentNote comes from the server. */}
                {m.absentNote || `${absentCount.toLocaleString()} items on the board were not in this import`}
                {absentSample.length ? (
                  <>
                    {` - named here: ${absentSample.length.toLocaleString()} of ${absentCount.toLocaleString()}: `}
                    <b>{absentSample.join(", ")}</b>
                    {absentCount > absentSample.length ? "…" : ""}
                  </>
                ) : null}
                . They are left exactly as they are — a source being
                quiet about a run is not evidence the run is gone.
              </div>
            )}
            <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end", gap: 8 }}>
              {/* ONE CLICK FOR THE WHOLE IMPORT. A big selection arrives
                  as several parts, and applying them by hand is how
                  production died twice on 2026-10-02 - eight at once,
                  each passing its own lock, all allocating in a 2 GB
                  heap. This applies the rest sequentially on the server.
                  Offered only when there IS a rest: groupOpenParts is
                  counted, so a group with one part left shows nothing. */}
              {canApply && onApplyGroup && canApplyGroup(m) && (
                <Tooltip title={`Applies the ${m.groupOpenParts} parts of this import that are still waiting, one after another. It can take a few minutes and cannot be run twice at once.`}>
                  <Button
                    loading={busy === `g:${m.groupId}`}
                    disabled={!!busy && busy !== `g:${m.groupId}`}
                    onClick={async () => {
                      setBusy(`g:${m.groupId}`);
                      try { await onApplyGroup(m.groupId, m.groupOpenParts); } finally { setBusy(""); }
                    }}
                  >
                    Apply whole group ({m.groupOpenParts} parts)
                  </Button>
                </Tooltip>
              )}
              {/* QUIET WHILE THE GROUP IS IN FLIGHT. The second press is
                  what put nineteen concurrent applies in one 2 GB heap at
                  01:18 and eight at 09:10, every one a 502 - and a button
                  that looks pressable during a six-minute job is an
                  invitation to press it. */}
              {canApply ? (<Tooltip title={isProcessing(m) ? processingLabel(m) : ""}>
                <Button
                  type="primary" loading={busy === String(m._id)}
                  disabled={isProcessing(m) || (!!busy && busy !== String(m._id))}
                  onClick={async () => {
                    setBusy(String(m._id));
                    try {
                      const list = Object.entries(mine).map(([observationKey, d]) => ({ observationKey, ...d }));
                      await onApply(m._id, list);
                    } finally { setBusy(""); }
                  }}
                >
                  Apply this source
                </Button>
              </Tooltip>) : null}
            </div>
            {/* ONE SENTENCE UNDER THE ACCEPT BUTTON, in the owner's words
                (2026-10-02). It appears only when the LAST run of this
                group failed - a group that failed and then succeeded says
                nothing, because a notification coming back on finished
                work is worse than none. The technical reason sits beside
                it rather than inside it: the sentence is what a person
                acts on, the reason is what whoever debugs it needs. */}
            {failureNote(m) ? (
              <div style={{ marginTop: 8, padding: "6px 10px", border: "1px solid #FFCCC7", background: "#FFF2F0", borderRadius: 4 }}>
                <div style={{ fontSize: 12.5, color: "#A8071A" }}>{failureNote(m).sentence}</div>
                {failureNote(m).reason ? (
                  <div style={{ fontSize: 11.5, color: "#8D8D8D", marginTop: 3, lineHeight: 1.45 }}>
                    {failureNote(m).reason}
                  </div>
                ) : null}
              </div>
            ) : null}
          </Collapse.Panel>
        );
      })}
    </Collapse>
  );
}
